/**
 * ERPRoutes Component
 * 
 * Componente que gerencia todas as rotas internas do ERP.
 * Utiliza o ERPLayout como wrapper e define as rotas específicas.
 */

import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import ERPLayout from '../../layouts/ERPLayout/ERPLayout'
import SmartRedirect from '../../components/SmartRedirect'
import ErpRouteGuard from '../../components/ErpRouteGuard'
import { usePermissions, MODULES, ACTIONS } from '../../hooks/usePermissions'
import { useAuth } from '../../contexts/AuthContext'
import { isHomeOrganization } from '../../utils/homeOrganization'
import { getFirstAccessibleRoute } from '../../utils/getFirstAccessibleRoute'
import ERPDashboardPage from './Dashboard/ERPDashboardPage'
import AtividadesPage from './Dashboard/AtividadesPage'
import ClientesPage from './Pessoas/Clientes/ClientesPage'
import ImportClientesPage from './Pessoas/Clientes/ImportClientesPage'
import ClienteEditPage from './Pessoas/Clientes/ClienteEditPage'
import FornecedoresPage from './Pessoas/Fornecedores/FornecedoresPage'
import GruposAcessoPage from './Pessoas/GruposAcesso/GruposAcessoPage'
import PermissionConfigPage from './Pessoas/GruposAcesso/PermissionConfigPage'
import UsuariosPage from './Pessoas/Usuarios/UsuariosPage'
import GruposPage from './Produtos/Grupos/GruposPage'
import MarcasPage from './Produtos/Marcas/MarcasPage'
import UnidadesPage from './Produtos/Unidades/UnidadesPage'
import MovimentacoesPage from './Estoque/Movimentacoes/MovimentacoesPage'
import NovaMovimentacaoPage from './Estoque/Movimentacoes/NovaMovimentacaoPage'
import ReturnsPage from './Estoque/TrocasDevolucoes/ReturnsPage'
import ReturnsCreatePage from './Estoque/TrocasDevolucoes/ReturnsCreatePage'
import ReturnsDetailPage from './Estoque/TrocasDevolucoes/ReturnsDetailPage'
// Páginas de Entrada de Mercadoria
import IncomingDashboard from './Estoque/EntradaMercadoria/IncomingDashboard'
import IncomingValidationPage from './Estoque/EntradaMercadoria/IncomingValidationPage'
import IncomingInspection from './Estoque/EntradaMercadoria/IncomingInspection'
import IncomingLaunchPage from './Estoque/EntradaMercadoria/IncomingLaunchPage'
import IncomingAddressingPage from './Estoque/EntradaMercadoria/IncomingAddressingPage'
import IncomingPricingPage from './Estoque/EntradaMercadoria/IncomingPricingPage'
import IncomingAccountsPayablePage from './Estoque/EntradaMercadoria/IncomingAccountsPayablePage'
import IncomingViewPage from './Estoque/EntradaMercadoria/IncomingViewPage'
// Páginas de Gestão de Endereços
import LocationsManagerPage from './Estoque/LocationsManagerPage'
// Páginas de Auditoria de Estoque
import AuditDashboard from './Estoque/Auditoria/AuditDashboard'
import ListAuditPage from './Estoque/Auditoria/ListAuditPage'
import IncomingAuditPage from './Estoque/Auditoria/IncomingAuditPage'
import AuditManagementPage from './Estoque/Auditoria/AuditManagementPage'
// Páginas de Ajuste de Estoque
import StockAdjustmentDashboard from './Estoque/AjusteEstoque/StockAdjustmentDashboard'
import StockAdjustmentCreatePage from './Estoque/AjusteEstoque/StockAdjustmentCreatePage'
import StockAdjustmentDetailPage from './Estoque/AjusteEstoque/StockAdjustmentDetailPage'
import ProdutosPage from './Produtos/Produtos/ProdutosPage'
import ProductCreationRequestDashboard from './Produtos/SolicitacoesCriacao/ProductCreationRequestDashboard'
import ProductCreationRequestFormPage from './Produtos/SolicitacoesCriacao/ProductCreationRequestFormPage'
import ProductCreationRequestDetailPage from './Produtos/SolicitacoesCriacao/ProductCreationRequestDetailPage'
import ProductDetailPage from './Produtos/Produtos/ProductDetailPage'
import GradeProdutosPage from './Produtos/Grades/GradeProdutosPage'
import EtiquetasPage from './Produtos/Etiquetas/EtiquetasPage'
import ContasReceberPage from './Financeiro/ContasReceber/ContasReceberPage'
import ContasPagarPage from './Financeiro/ContasPagar/ContasPagarPage'
import BoletosListPage from './Financeiro/Boletos/BoletosListPage'
import BoletosDashboard from './Financeiro/Boletos/BoletosDashboard'
import NovaContaPagarPage from './Financeiro/ContasPagar/NovaContaPagarPage'
import CategoriasFinanceirasPage from './Financeiro/CategoriasFinanceiras/CategoriasFinanceirasPage'
import CentrosCustoPage from './Financeiro/CentrosCusto/CentrosCustoPage'
import ExtratoCaixaPage from './Financeiro/ExtratoCaixa/CashStatementPage'
import ConciliacaoCartoesPage from './Financeiro/ConciliacaoCartoes/ConciliacaoCartoesPage'
import ContasPage from './Financeiro/Contas/ContasPage'
import FormasRecebimentoPage from './Financeiro/FormasRecebimento/FormasRecebimentoPage'
import FinanceDashboardPage from './Financeiro/Dashboard/FinanceDashboardPage'
import ErpSettingsHubPage from './Configuracoes/ErpSettingsHubPage'
import RelatoriosHubPage from './Relatorios/RelatoriosHubPage'
import ContasReceberReportPage from './Relatorios/ContasReceber/ContasReceberReportPage'
import EstoqueReportPage from './Relatorios/Estoque/EstoqueReportPage'
import VendasReportPage from './Relatorios/Vendas/VendasReportPage'
import CockpitSettingsPage from './Configuracoes/CockpitSettingsPage'
import StorefrontSettingsPage from './Configuracoes/StorefrontSettingsPage'
import HeroSettingsPage from './Configuracoes/HeroSettingsPage'
import FretePorCepPage from './Configuracoes/FretePorCepPage'
import CuponsPage from './Configuracoes/CuponsPage'
import CertificadosPage from './Configuracoes/CertificadosPage'
import CashFlowAnalysisPage from './Financeiro/Dashboard/CashFlowAnalysisPage'
import PayablesAnalysisPage from './Financeiro/PayablesAnalysis/PayablesAnalysisPage'
import DREPage from './Financeiro/DRE/DREPage'
import DFCPage from './Financeiro/DFC/DFCPage'
import BalancoPage from './Financeiro/Balanco/BalancoPage'
import TeamDashboardPage from './Cockpit/TeamDashboardPage'
// Páginas de Vendas
// VendasPage e PreVendaPage estão no App.jsx (sem layout)
import RelacaoVendasPage from './Vendas/RelacaoVendas/RelacaoVendasPage'
import RelacaoPreVendaPage from './Vendas/PreVendas/PreVendasPage' // Temporário - será refatorado
import ConferenciaCaixaListPage from './Vendas/ConferenciaCaixa/ConferenciaCaixaListPage'
import ConferenciaCaixaDetailPage from './Vendas/ConferenciaCaixa/ConferenciaCaixaDetailPage'
import OnlineSalesPage from './Vendas/VendasOnline/OnlineSalesPage'
import DocumentsDashboard from './Documents/DocumentsDashboard'
import CompanyDocumentsPage from './Documents/CompanyDocumentsPage'
import UserDocumentsPage from './Documents/UserDocumentsPage'
import UserDocumentsViewPage from './Documents/UserDocumentsViewPage'
import UserDocumentsDetailPage from './Documents/UserDocumentsDetailPage'
import DocumentCategoriesPage from './Documents/DocumentCategoriesPage'
import AttendanceReportsPage from './Documents/AttendanceReportsPage'
import AttendanceLogPage from './Documents/AttendanceLogPage'
// Páginas de Orçamentos
import OrcamentosPage from './Orcamentos/OrcamentosPage'
import DashboardOrcamentosPage from './Orcamentos/DashboardOrcamentosPage'
import NovoOrcamentoPage from './Orcamentos/NovoOrcamentoPage'
import OrcamentoDetailPage from './Orcamentos/OrcamentoDetailPage'
// Páginas de NF-e
import NFeListPage from './Vendas/NFe/NFeListPage'
import NFeFinalizePage from './Vendas/NFe/NFeFinalizePage'
import NFeFromQuotePage from './Vendas/NFe/NFeFromQuotePage'
import NFeEditPage from './Vendas/NFe/NFeEditPage'
// Páginas de Tabelas Especiais
import SpecialTablesListPage from './Vendas/TabelasEspeciais/SpecialTablesListPage'
import SpecialTableEditPage from './Vendas/TabelasEspeciais/SpecialTableEditPage'
// Páginas de Ponto Eletrônico
import AttendancePage from './PontoEletronico/AttendancePage'
import PatientsListPage from './Pacientes/PatientsListPage'
import PatientsDashboardPage from './Pacientes/PatientsDashboardPage'
import DutyRosterPage from './Plantao/DutyRosterPage'
import MyDutyPage from './Plantao/MyDutyPage'
import PatientRecordPage from './Pacientes/PatientRecordPage'
// Páginas de Prospecção
import ProspectingPage from './Prospecting/ProspectingPage'
import NewProposalPage from './Prospecting/NewProposalPage'
import LeadProposalsPage from './Prospecting/LeadProposalsPage'
import LeadContactsPage from './Prospecting/LeadContactsPage'
import LeadDetailCommunicationsPage from './Prospecting/LeadDetailCommunicationsPage'
// Páginas de Compras
import PurchasesDashboard from './Compras/PurchasesDashboard'
import NewRequestPage from './Compras/NewRequestPage'
import ReviewListPage from './Compras/ReviewListPage'
import DraftListPage from './Compras/DraftListPage'
import ReviewAnalysisPage from './Compras/ReviewAnalysisPage'
import MirrorListPage from './Compras/MirrorListPage'
import MirrorVerificationPage from './Compras/MirrorVerificationPage'
import FinanceApprovalListPage from './Compras/FinanceApprovalListPage'
import FinanceApprovalPage from './Compras/FinanceApprovalPage'
import FollowUpPage from './Compras/FollowUpPage'
import MinimumStockPage from './Compras/MinimumStockPage'
import CompraInteligenteDashboardPage from './Compras/Intelligence/CompraInteligenteDashboardPage'
import IntelligenceCreateDraftsPage from './Compras/Intelligence/IntelligenceCreateDraftsPage'
import MensagensPage from './Mensagens/MensagensPage'
import SellerAssistantPage from './Vendas/AssistenteVendas/SellerAssistantPage'
import OperationalAssistantPage from './AssistenteOperacional/OperationalAssistantPage'
import TVDisplayPage from './Vendas/TVDisplay/TVDisplayPage'
import PropositoEmAcaoPage from './PropositoEmAcao/PropositoEmAcaoPage'
import PerfilPage from './Perfil/PerfilPage'

function HomeOrganizationOnly({ children }) {
  const { organizations, currentSchema, user } = useAuth()
  const schema = currentSchema || user?.organization_schema
  if (!isHomeOrganization(organizations, schema)) {
    return <Navigate to="/erp/dashboard" replace />
  }
  return children
}

function ERPContent() {
  const { hasPermission, isAdmin, loading } = usePermissions()

  // Se ainda está carregando, mostrar loading
  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Carregando...</div>
  }

  // Encontrar primeira rota acessível para redirecionamento padrão (relativa)
  const defaultRoute = getFirstAccessibleRoute(hasPermission, isAdmin, true)

  return (
    <Routes>
      <Route 
        path="dashboard" 
        element={
          <SmartRedirect requiredModule={MODULES.DASHBOARD} requiredAction={ACTIONS.VIEW}>
            <ERPDashboardPage />
          </SmartRedirect>
        } 
      />
      <Route
        path="dashboard/atividades"
        element={
          <SmartRedirect requiredModule={MODULES.DASHBOARD} requiredAction={ACTIONS.VIEW}>
            <AtividadesPage />
          </SmartRedirect>
        }
      />
      <Route 
        path="cockpit" 
        element={
          <SmartRedirect requiredModule={MODULES.COCKPIT} requiredAction={ACTIONS.VIEW}>
            <TeamDashboardPage />
          </SmartRedirect>
        } 
      />
      <Route path="pessoas/clientes" element={<ClientesPage />} />
      <Route path="pessoas/clientes/importar" element={<ImportClientesPage />} />
      <Route path="pessoas/clientes/novo" element={<ClienteEditPage />} />
      <Route path="pessoas/clientes/:id/editar" element={<ClienteEditPage />} />
      <Route path="pessoas/fornecedores" element={<FornecedoresPage />} />
      <Route path="pessoas/usuarios" element={<UsuariosPage />} />
      <Route path="perfil" element={<PerfilPage />} />
      <Route 
        path="pessoas/grupos" 
        element={
          <SmartRedirect requiredModule={MODULES.PEOPLE} requiredAction={ACTIONS.PEOPLE_ACCESS_GROUPS}>
            <GruposAcessoPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="pessoas/grupos/:id/permissions" 
        element={
          <SmartRedirect requiredModule={MODULES.PEOPLE} requiredAction={ACTIONS.PEOPLE_ACCESS_GROUPS}>
            <PermissionConfigPage />
          </SmartRedirect>
        } 
      />
      <Route path="produtos/produtos" element={<ProdutosPage />} />
      <Route path="produtos/produtos/:id" element={<ProductDetailPage />} />
      <Route
        path="produtos/etiquetas"
        element={
          <SmartRedirect requiredModule={MODULES.PRODUCTS} requiredAction={ACTIONS.VIEW}>
            <EtiquetasPage />
          </SmartRedirect>
        }
      />
      <Route
        path="produtos/solicitacoes"
        element={
          <SmartRedirect requiredModule={MODULES.PRODUCTS} requiredAction={ACTIONS.PRODUCT_REQUEST_VIEW}>
            <ProductCreationRequestDashboard />
          </SmartRedirect>
        }
      />
      <Route
        path="produtos/solicitacoes/nova"
        element={
          <SmartRedirect requiredModule={MODULES.PRODUCTS} requiredAction={ACTIONS.PRODUCT_REQUEST_CREATE}>
            <ProductCreationRequestFormPage />
          </SmartRedirect>
        }
      />
      <Route
        path="produtos/solicitacoes/:id"
        element={
          <SmartRedirect requiredModule={MODULES.PRODUCTS} requiredAction={ACTIONS.PRODUCT_REQUEST_VIEW}>
            <ProductCreationRequestDetailPage />
          </SmartRedirect>
        }
      />
      <Route
        path="produtos/solicitacoes/:id/editar"
        element={
          <SmartRedirect requiredModule={MODULES.PRODUCTS} requiredAction={ACTIONS.PRODUCT_REQUEST_VIEW}>
            <ProductCreationRequestFormPage />
          </SmartRedirect>
        }
      />
      <Route 
        path="produtos/grades" 
        element={
          <SmartRedirect requiredModule={MODULES.PRODUCTS} requiredAction={ACTIONS.PRODUCTS_GRIDS}>
            <GradeProdutosPage />
          </SmartRedirect>
        } 
      />
      <Route path="produtos/grupos" element={<GruposPage />} />
      <Route path="produtos/marcas" element={<MarcasPage />} />
      <Route path="produtos/unidades" element={<UnidadesPage />} />
      <Route path="estoque/movimentacoes" element={<MovimentacoesPage />} />
      <Route path="estoque/movimentacoes/nova" element={<NovaMovimentacaoPage />} />
      {/* Rotas de Entrada de Mercadoria */}
      <Route path="stock/incoming" element={<IncomingDashboard />} />
      <Route path="stock/incoming/validation/:id" element={<IncomingValidationPage />} />
      <Route path="stock/incoming/inspection/:id" element={<IncomingInspection />} />
      <Route path="stock/incoming/launch/:id" element={<IncomingLaunchPage />} />
      <Route path="stock/incoming/addressing/:id" element={<IncomingAddressingPage />} />
      <Route path="stock/incoming/pricing/:id" element={<IncomingPricingPage />} />
      <Route path="stock/incoming/accounts-payable/:id" element={<IncomingAccountsPayablePage />} />
      <Route path="stock/incoming/view/:id" element={<IncomingViewPage />} />
      {/* Rotas de Gestão de Endereços */}
      <Route path="stock/locations" element={<LocationsManagerPage />} />
      {/* Rotas de Auditoria de Estoque */}
      <Route 
        path="stock/audits" 
        element={
          <SmartRedirect requiredModule={MODULES.STOCK} requiredAction={ACTIONS.STOCK_AUDIT_VIEW}>
            <AuditDashboard />
          </SmartRedirect>
        } 
      />
      <Route 
        path="stock/audits/list" 
        element={
          <SmartRedirect requiredModule={MODULES.STOCK} requiredAction={ACTIONS.STOCK_AUDIT_VIEW}>
            <ListAuditPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="stock/audits/:id" 
        element={
          <SmartRedirect requiredModule={MODULES.STOCK} requiredAction={ACTIONS.STOCK_AUDIT_EXECUTE}>
            <IncomingAuditPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="stock/audits/:id/manage" 
        element={
          <SmartRedirect requiredModule={MODULES.STOCK} requiredAction={ACTIONS.STOCK_AUDIT_VIEW}>
            <AuditManagementPage />
          </SmartRedirect>
        } 
      />
      <Route path="estoque/trocas" element={<ReturnsPage />} />
      <Route path="estoque/trocas/nova" element={<ReturnsCreatePage />} />
      <Route path="estoque/trocas/:id" element={<ReturnsDetailPage />} />
      <Route path="estoque/trocas/:id/editar" element={<ReturnsCreatePage />} />
      {/* Rotas de Ajuste de Estoque Físico/Digital */}
      <Route 
        path="stock/adjustments" 
        element={
          <SmartRedirect requiredModule={MODULES.STOCK} requiredAction={ACTIONS.STOCK_ADJUSTMENT_VIEW}>
            <StockAdjustmentDashboard />
          </SmartRedirect>
        } 
      />
      <Route 
        path="stock/adjustments/new" 
        element={
          <SmartRedirect requiredModule={MODULES.STOCK} requiredAction={ACTIONS.STOCK_ADJUSTMENT_CREATE}>
            <StockAdjustmentCreatePage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="stock/adjustments/:id" 
        element={
          <SmartRedirect requiredModule={MODULES.STOCK} requiredAction={ACTIONS.STOCK_ADJUSTMENT_VIEW}>
            <StockAdjustmentDetailPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="stock/adjustments/:id/edit" 
        element={
          <SmartRedirect requiredModule={MODULES.STOCK} requiredAction={ACTIONS.STOCK_ADJUSTMENT_VIEW}>
            <StockAdjustmentCreatePage />
          </SmartRedirect>
        } 
      />
      <Route path="financeiro/contas-receber" element={<ContasReceberPage />} />
      <Route path="financeiro/contas-pagar" element={<ContasPagarPage />} />
      <Route
        path="financeiro/boletos/dashboard"
        element={
          <SmartRedirect requiredModule={MODULES.FINANCIAL} requiredAction={ACTIONS.FINANCIAL_BOLETOS_DASHBOARD}>
            <BoletosDashboard />
          </SmartRedirect>
        }
      />
      <Route
        path="financeiro/boletos"
        element={
          <SmartRedirect requiredModule={MODULES.FINANCIAL} requiredAction={ACTIONS.FINANCIAL_BOLETOS_VIEW}>
            <BoletosListPage />
          </SmartRedirect>
        }
      />
      <Route path="financeiro/contas-pagar/nova" element={<NovaContaPagarPage />} />
      <Route path="financeiro/categorias" element={<CategoriasFinanceirasPage />} />
      <Route path="financeiro/centros-custo" element={<CentrosCustoPage />} />
      <Route path="financeiro/extrato-caixa" element={<ExtratoCaixaPage />} />
      <Route path="financeiro/conciliacao-cartoes" element={<ConciliacaoCartoesPage />} />
      <Route path="financeiro/contas" element={<ContasPage />} />
      <Route path="financeiro/formas-recebimento" element={<FormasRecebimentoPage />} />
      <Route path="financeiro/dashboard" element={<FinanceDashboardPage />} />
      <Route
        path="relatorios"
        element={
          <SmartRedirect>
            <RelatoriosHubPage />
          </SmartRedirect>
        }
      />
      <Route
        path="relatorios/contas-receber"
        element={
          <SmartRedirect>
            <ContasReceberReportPage />
          </SmartRedirect>
        }
      />
      <Route
        path="relatorios/estoque"
        element={
          <SmartRedirect>
            <EstoqueReportPage />
          </SmartRedirect>
        }
      />
      <Route
        path="relatorios/vendas"
        element={
          <SmartRedirect>
            <VendasReportPage />
          </SmartRedirect>
        }
      />
      <Route
        path="configuracoes"
        element={
          <SmartRedirect requiredModule={MODULES.SETTINGS} requiredAction={ACTIONS.VIEW}>
            <ErpSettingsHubPage />
          </SmartRedirect>
        }
      />
      <Route
        path="configuracoes/cockpit"
        element={
          <SmartRedirect requiredModule={MODULES.SETTINGS} requiredAction={ACTIONS.VIEW}>
            <CockpitSettingsPage />
          </SmartRedirect>
        }
      />
      <Route
        path="configuracoes/hero"
        element={
          <SmartRedirect requiredModule={MODULES.SETTINGS} requiredAction={ACTIONS.VIEW}>
            <HeroSettingsPage />
          </SmartRedirect>
        }
      />
      <Route
        path="configuracoes/vitrine"
        element={
          <SmartRedirect requiredModule={MODULES.SETTINGS} requiredAction={ACTIONS.VIEW}>
            <StorefrontSettingsPage />
          </SmartRedirect>
        }
      />
      <Route
        path="configuracoes/frete-cep"
        element={
          <SmartRedirect requiredModule={MODULES.SETTINGS} requiredAction={ACTIONS.VIEW}>
            <FretePorCepPage />
          </SmartRedirect>
        }
      />
      <Route
        path="configuracoes/cupons"
        element={
          <SmartRedirect requiredModule={MODULES.SETTINGS} requiredAction={ACTIONS.VIEW}>
            <CuponsPage />
          </SmartRedirect>
        }
      />
      <Route
        path="configuracoes/certificados"
        element={
          <SmartRedirect
            requiredModule={MODULES.DIGITAL_CERTIFICATE}
            requiredAction={ACTIONS.DIGITAL_CERTIFICATE_MANAGE}
            alsoModule={MODULES.SETTINGS}
            alsoAction={ACTIONS.SETTINGS_CERTIFICATES}
          >
            <CertificadosPage />
          </SmartRedirect>
        }
      />
      <Route path="financeiro/analise-fluxo-caixa" element={<CashFlowAnalysisPage />} />
      <Route path="financeiro/analise-contas-pagar" element={<PayablesAnalysisPage />} />
      <Route path="financeiro/dre" element={<DREPage />} />
      <Route path="financeiro/dfc" element={<DFCPage />} />
      <Route path="financeiro/balanco" element={<BalancoPage />} />
      {/* Rotas de Vendas */}
      {/* vendas/vendas e vendas/pre-venda estão no App.jsx (sem layout) */}
      <Route
        path="vendas/relacao-vendas"
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_RELATION_VIEW}>
            <RelacaoVendasPage />
          </SmartRedirect>
        }
      />
      <Route
        path="vendas/relacao-pre-venda"
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_PRESALE_LIST_VIEW}>
            <RelacaoPreVendaPage />
          </SmartRedirect>
        }
      />
      <Route
        path="vendas/pre-vendas"
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_PRESALE_LIST_VIEW}>
            <RelacaoPreVendaPage />
          </SmartRedirect>
        }
      />
      <Route 
        path="vendas/online" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_ONLINE_VIEW}>
            <OnlineSalesPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="vendas/caixa" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_CASH_REGISTER_RECONCILIATION}>
            <ConferenciaCaixaListPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="vendas/conferencia-caixa/:id" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_CASH_REGISTER_RECONCILIATION}>
            <ConferenciaCaixaDetailPage />
          </SmartRedirect>
        } 
      />
      {/* Rotas de Documentos */}
      <Route path="documents/dashboard" element={<DocumentsDashboard />} />
      <Route path="documents/company" element={<CompanyDocumentsPage />} />
      <Route path="documents/my-documents" element={<UserDocumentsPage />} />
      <Route path="documents/users" element={<UserDocumentsViewPage />} />
      <Route path="documents/users/:userId" element={<UserDocumentsDetailPage />} />
      <Route 
        path="documents/categories" 
        element={
          <SmartRedirect requiredModule={MODULES.DOCUMENTS} requiredAction={ACTIONS.DOCUMENTS_CATEGORIES_VIEW}>
            <DocumentCategoriesPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="documents/attendance-reports" 
        element={
          <SmartRedirect requiredModule={MODULES.ATTENDANCE} requiredAction={ACTIONS.ATTENDANCE_ACCOUNTING}>
            <AttendanceReportsPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="documents/attendance-log"
        element={
          <SmartRedirect requiredModule={MODULES.ATTENDANCE} requiredAction={ACTIONS.ATTENDANCE_LOG}>
            <AttendanceLogPage />
          </SmartRedirect>
        }
      />
      {/* Rotas de Orçamentos */}
      <Route 
        path="orcamentos" 
        element={
          <SmartRedirect requiredModule={MODULES.QUOTES} requiredAction={ACTIONS.VIEW}>
            <OrcamentosPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="orcamentos/dashboard" 
        element={
          <SmartRedirect requiredModule={MODULES.QUOTES} requiredAction={ACTIONS.VIEW}>
            <DashboardOrcamentosPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="orcamentos/novo" 
        element={
          <SmartRedirect requiredModule={MODULES.QUOTES} requiredAction={ACTIONS.CREATE}>
            <NovoOrcamentoPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="orcamentos/:id" 
        element={
          <SmartRedirect requiredModule={MODULES.QUOTES} requiredAction={ACTIONS.VIEW}>
            <OrcamentoDetailPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="orcamentos/:id/editar" 
        element={
          <SmartRedirect requiredModule={MODULES.QUOTES} requiredAction={ACTIONS.UPDATE}>
            <NovoOrcamentoPage />
          </SmartRedirect>
        } 
      />
      {/* Rotas de NF-e Modelo 55 */}
      <Route 
        path="vendas/nfe" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_NFE_VIEW}>
            <NFeListPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="vendas/nfe/nova/:saleId" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_NFE_CREATE}>
            <NFeFinalizePage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="vendas/nfe/from-quote/:quoteId" 
        element={
          <SmartRedirect requiredModule={MODULES.QUOTES} requiredAction={ACTIONS.QUOTES_CONVERT}>
            <NFeFromQuotePage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="vendas/nfe/:id/editar" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_NFE_UPDATE}>
            <NFeEditPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="vendas/nfe/:id" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_NFE_VIEW}>
            <NFeListPage />
          </SmartRedirect>
        } 
      />
      {/* Rotas de Tabelas Especiais de Preços */}
      <Route 
        path="vendas/tabelas-especiais" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_SPECIAL_TABLES_VIEW}>
            <SpecialTablesListPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="vendas/tabelas-especiais/nova" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_SPECIAL_TABLES_MANAGE}>
            <SpecialTableEditPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="vendas/tabelas-especiais/:id" 
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_SPECIAL_TABLES_VIEW}>
            <SpecialTableEditPage />
          </SmartRedirect>
        } 
      />
      <Route
        path="minha-escala"
        element={
          <SmartRedirect requiredModule={MODULES.MY_SHIFTS} requiredAction={ACTIONS.VIEW}>
            <MyDutyPage />
          </SmartRedirect>
        }
      />
      <Route
        path="plantoes"
        element={
          <SmartRedirect requiredModule={MODULES.DUTY_ROSTERS} requiredAction={ACTIONS.VIEW}>
            <DutyRosterPage />
          </SmartRedirect>
        }
      />
      <Route
        path="pacientes/painel"
        element={
          <SmartRedirect requiredModule={MODULES.PATIENTS} requiredAction={ACTIONS.PATIENTS_DASHBOARD}>
            <PatientsDashboardPage />
          </SmartRedirect>
        }
      />
      <Route
        path="pacientes"
        element={
          <SmartRedirect requiredModule={MODULES.PATIENTS} requiredAction={ACTIONS.VIEW}>
            <PatientsListPage />
          </SmartRedirect>
        }
      />
      <Route
        path="pacientes/:id"
        element={
          <SmartRedirect requiredModule={MODULES.PATIENTS} requiredAction={ACTIONS.VIEW}>
            <PatientRecordPage />
          </SmartRedirect>
        }
      />
      <Route path="patients" element={<Navigate to="/erp/pacientes" replace />} />
      <Route path="patients/:id" element={<LegacyPatientRedirect />} />
      {/* Rotas de Ponto Eletrônico */}
      <Route 
        path="ponto-eletronico" 
        element={
          <SmartRedirect requiredModule={MODULES.ATTENDANCE} requiredAction={ACTIONS.VIEW}>
            <AttendancePage />
          </SmartRedirect>
        } 
      />
      {/* Rotas de Prospecção */}
      <Route 
        path="prospecting" 
        element={
          <SmartRedirect requiredModule={MODULES.PROSPECTING} requiredAction={ACTIONS.PROSPECTING_VIEW}>
            <ProspectingPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="prospecting/contacts" 
        element={
          <SmartRedirect requiredModule={MODULES.PROSPECTING} requiredAction={ACTIONS.PROSPECTING_VIEW}>
            <LeadContactsPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="prospecting/contacts/:id" 
        element={
          <SmartRedirect requiredModule={MODULES.PROSPECTING} requiredAction={ACTIONS.PROSPECTING_VIEW}>
            <LeadDetailCommunicationsPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="prospecting/proposals/new" 
        element={
          <SmartRedirect requiredModule={MODULES.PROSPECTING} requiredAction={ACTIONS.PROSPECTING_PROPOSALS}>
            <NewProposalPage />
          </SmartRedirect>
        } 
      />
      <Route 
        path="prospecting/leads/:leadId/proposals" 
        element={
          <SmartRedirect requiredModule={MODULES.PROSPECTING} requiredAction={ACTIONS.PROSPECTING_PROPOSALS}>
            <LeadProposalsPage />
          </SmartRedirect>
        } 
      />
      {/* Rotas de Compras */}
      <Route path="purchases" element={<ErpRouteGuard><PurchasesDashboard /></ErpRouteGuard>} />
      <Route path="purchases/new-request/:id?" element={<ErpRouteGuard><NewRequestPage /></ErpRouteGuard>} />
      <Route path="purchases/draft-list" element={<ErpRouteGuard><DraftListPage /></ErpRouteGuard>} />
      <Route path="purchases/review-list" element={<ErpRouteGuard><ReviewListPage /></ErpRouteGuard>} />
      <Route path="purchases/review/:id" element={<ErpRouteGuard><ReviewAnalysisPage /></ErpRouteGuard>} />
      <Route path="purchases/mirrors" element={<ErpRouteGuard><MirrorListPage /></ErpRouteGuard>} />
      <Route path="purchases/mirrors/verify/:id" element={<ErpRouteGuard><MirrorVerificationPage /></ErpRouteGuard>} />
      <Route
        path="purchases/finance"
        element={
          <SmartRedirect requiredModule={MODULES.FINANCIAL} requiredAction={ACTIONS.FINANCIAL_PURCHASE_APPROVAL}>
            <FinanceApprovalListPage />
          </SmartRedirect>
        }
      />
      <Route
        path="purchases/finance/approve/:id"
        element={
          <SmartRedirect requiredModule={MODULES.FINANCIAL} requiredAction={ACTIONS.FINANCIAL_PURCHASE_APPROVAL}>
            <FinanceApprovalPage />
          </SmartRedirect>
        }
      />
      <Route path="purchases/follow-up" element={<ErpRouteGuard><FollowUpPage /></ErpRouteGuard>} />
      <Route path="purchases/minimum-stock" element={<ErpRouteGuard><MinimumStockPage /></ErpRouteGuard>} />
      <Route path="purchases/intelligence" element={<ErpRouteGuard><CompraInteligenteDashboardPage /></ErpRouteGuard>} />
      <Route path="purchases/intelligence/create-drafts" element={<ErpRouteGuard><IntelligenceCreateDraftsPage /></ErpRouteGuard>} />
      {/* Rotas de Mensagens */}
      <Route 
        path="mensagens" 
        element={
          <SmartRedirect requiredModule={MODULES.MESSAGES} requiredAction={ACTIONS.VIEW}>
            <MensagensPage />
          </SmartRedirect>
        } 
      />
      {/* Assistente de Vendas */}
      <Route
        path="assistente-vendas"
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_SELLER_ASSISTANT}>
            <SellerAssistantPage />
          </SmartRedirect>
        }
      />
      <Route
        path="assistente-operacional"
        element={
          <SmartRedirect requiredModule={MODULES.ERP} requiredAction={ACTIONS.ERP_OPERATIONAL_ASSISTANT}>
            <OperationalAssistantPage />
          </SmartRedirect>
        }
      />
      {/* Vitrine TV — telão da loja */}
      <Route
        path="vendas/tv-display"
        element={
          <SmartRedirect requiredModule={MODULES.SALES} requiredAction={ACTIONS.SALES_TV_DISPLAY}>
            <TVDisplayPage />
          </SmartRedirect>
        }
      />
      {/* Rota de Propósito em Ação */}
      <Route
        path="proposito-em-acao"
        element={(
          <HomeOrganizationOnly>
            <PropositoEmAcaoPage />
          </HomeOrganizationOnly>
        )}
      />
      <Route
        path=""
        element={
          defaultRoute
            ? <Navigate to={defaultRoute} replace />
            : (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                Nenhuma tela está liberada para o seu grupo de acesso.
              </div>
            )
        }
      />
      {/* Outras rotas serão adicionadas aqui */}
    </Routes>
  )
}

function LegacyPatientRedirect() {
  const { id } = useParams()
  return <Navigate to={`/erp/pacientes/${id}`} replace />
}

function ERPRoutes() {
  return (
    <ERPLayout>
      <ERPContent />
    </ERPLayout>
  )
}

export default ERPRoutes

